import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, Skeleton } from '../ui';
import { getInspectionLifecycle, inspectionsKeys, type InspectionLifecycle } from '../../lib/api/inspections.gen';
import { listReports, reportsKeys } from '../../lib/api/reports.gen';
import { queryOrNull } from '../../lib/query/queryFn';
import { errorMessage } from '../../lib/errorMessage';
import { useCapabilityGate } from '../../lib/capabilities';

// Thin status router for `/app/items/:id` (story 5-12).
//
// The legacy `ItemDetailPage` fused photo upload, page-local AI-analysis
// output, job progress, "Generate report" and inline report viewing onto one
// screen. Every one of those jobs now has a real owner — capture (5-10a-e),
// review (5-11b), reports (4-9a) — so this component does the one thing that
// still legitimately belongs at the OLD url: read the inspection's lifecycle
// status and send the visitor to whichever of the three surfaces is
// authoritative for that status, `replace`d so Back does not bounce them into
// a legacy shell that no longer exists.
//
// Status → surface mapping (P05 §10 / story 5-12 sequencing note):
//   draft / scheduled / assigned / in_progress → capture (`/app/inspections/:id/capture`)
//   submitted / in_review                       → review  (`/app/review/:id`)
//   approved / delivered                        → report  (`/app/reports/:reportId`)
// `cancelled` and the pre-migration-012 legacy `completed` value are not named
// by the mapping above (only nine states minus these two are). Both are
// terminal, read-only states with no dedicated "cancelled" or "legacy
// completed" screen anywhere in the product, so they fall back to the nearest
// surface that already renders a read-only terminal inspection: capture opens
// a non-performer read-only (P05 §9.2 — "a reviewer and a manager may
// legitimately open capture to look at a submitted inspection read-only";
// server-driven `readOnly` covers `cancelled` too) and `completed` is folded
// into the `approved` bucket, matching migration 012's own mapping
// (`ItemsListPage.tsx`'s STATUS_VALUES comment) rather than inventing a
// second meaning for a value the data no longer produces going forward.
const CAPTURE_STATUSES = new Set<InspectionLifecycle['status']>([
  'draft',
  'scheduled',
  'assigned',
  'in_progress',
  'cancelled',
]);
const REVIEW_STATUSES = new Set<InspectionLifecycle['status']>(['submitted', 'in_review']);
const REPORT_STATUSES = new Set<InspectionLifecycle['status']>(['approved', 'delivered', 'completed']);

// Mirrors `reportLegacyRedirectKey` (modules/reports/queries/keys.ts) — a
// named factory rather than an inline array at the call site (G-19), for the
// same reason: the report-by-inspection point lookup this router does is the
// mirror image of that module's own legacy-link redirect.
const reportByInspectionKey = (inspectionId: string): readonly unknown[] => [
  ...reportsKeys.lists(),
  'by-inspection',
  inspectionId,
];

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading inspection">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function InspectionStatusRouter(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // `GET .../lifecycle` sits behind `scheduling.core` (P05 §1/§5) — the SAME
  // flag `InspectionLifecycleBar` checks before firing its own read. Without
  // this gate, an org with the flag off would 403 the lifecycle read this
  // router needs to pick a destination and land on an "Inspection not found"
  // error for a row that exists — the affordance is meant to disappear, not
  // the surface. Capture's own gate is the unrelated `inspections.core`
  // (`modules/capture/index.ts`), so it renders fine regardless — the
  // fallback destination when lifecycle-based routing is unavailable.
  const schedulingGate = useCapabilityGate('scheduling.core');
  const lifecycleReadable = schedulingGate.isHydrated && schedulingGate.allowed;

  const lifecycleQuery = useQuery({
    queryKey: inspectionsKeys.detail(id),
    queryFn: async () => (await queryOrNull(getInspectionLifecycle(id)))?.data ?? null,
    enabled: id !== '' && lifecycleReadable,
    retry: false,
  });

  const status = lifecycleQuery.data?.status;
  const needsReportLookup = status !== undefined && REPORT_STATUSES.has(status);

  // Point lookup by a unique `inspectionId`, not a list surface to page
  // through — the same G-18 false positive `ReportDetailPage`'s own
  // legacy-link redirect already documents (that redirect is this router's
  // mirror image: theirs resolves an inspection id typed where a report id
  // belongs, this one resolves the report id `approved`/`delivered` implies).
  // eslint-disable-next-line no-restricted-syntax
  const reportQuery = useQuery({
    queryKey: reportByInspectionKey(id),
    queryFn: () => listReports({ inspectionId: id, limit: 1 }),
    enabled: needsReportLookup,
    retry: false,
  });

  if (!schedulingGate.isHydrated) {
    return <LoadingSkeleton />;
  }

  // scheduling.core is off/not entitled — no lifecycle read is possible, so
  // there is no status to route on. Capture is the one surface that renders
  // on its own, unrelated gate; land there rather than showing a false
  // "not found" for a row that is simply missing this one flag.
  if (!schedulingGate.allowed) {
    return <Navigate to={`/app/inspections/${id}/capture`} replace />;
  }

  if (lifecycleQuery.isLoading || (needsReportLookup && reportQuery.isLoading)) {
    return <LoadingSkeleton />;
  }

  if (lifecycleQuery.isError || !lifecycleQuery.data || status === undefined) {
    return (
      <EmptyState
        title="Inspection not found"
        description={
          lifecycleQuery.isError
            ? errorMessage(lifecycleQuery.error)
            : "This inspection no longer exists, or you don't have access to it."
        }
        actionLabel="Back to inspections"
        onAction={() => navigate('/app/items')}
      />
    );
  }

  if (CAPTURE_STATUSES.has(status)) {
    return <Navigate to={`/app/inspections/${id}/capture`} replace />;
  }

  if (REVIEW_STATUSES.has(status)) {
    return <Navigate to={`/app/review/${id}`} replace />;
  }

  // REPORT_STATUSES — approved / delivered / (legacy) completed. The report
  // id is not the inspection id, so it takes the same point lookup
  // `ReportDetailPage`'s legacy redirect uses in the other direction.
  const reportId = reportQuery.data?.items[0]?._id;
  if (reportId) {
    return <Navigate to={`/app/reports/${reportId}`} replace />;
  }

  // Approved/delivered with no report row yet is not supposed to happen —
  // assembly runs on submit (5-11a), well before approval — but this is a
  // status router, not the assembler; it must not silently hang (G-29) if the
  // data is ever in that shape. Send the visitor to review, the next-nearest
  // surface that still shows the real inspection, rather than a dead end.
  return (
    <EmptyState
      title="Report not ready yet"
      description="This inspection has no report on file yet. You can view it in the review workspace instead."
      actionLabel="Open in review"
      onAction={() => navigate(`/app/review/${id}`)}
    />
  );
}
